/**
 * RecordCount — standardised record-count bar used across all directory /
 * listing pages.  Renders a single `<div>` with text + optional action links.
 *
 * Display logic (mirrors the Bills / Legislation page pattern):
 *
 *   Filters active:
 *     "{filtered} {label} match criteria (showing {showing}) · {total} total {label}"
 *
 *   No filters:
 *     "{total} {label} found (showing {showing})"
 *
 *   When showing === filtered (i.e. all results displayed), the "(showing …)"
 *   part is omitted to avoid redundancy.
 *
 *   "Show All" / "Show Paginated" links appear when applicable.
 */

import React from 'react';

export interface RecordCountProps {
  /** Human-readable plural label, e.g. "bills", "legislators", "candidates" */
  label: string;
  /** Number of records currently rendered on screen */
  showing: number;
  /** Number of records that match the active filters (may equal `total`) */
  filtered: number;
  /** Total records in the data set (unfiltered). If omitted, treated as === filtered. */
  total?: number;
  /** Whether filters / search are currently active */
  hasActiveFilters?: boolean;
  /** If true, a loading spinner is shown before the count text */
  isLoading?: boolean;
  /** Called when the user clicks "Show All" */
  onShowAll?: () => void;
  /** Called when the user clicks "Show Paginated" */
  onShowPaginated?: () => void;
  /** Whether we are currently in "show all" mode */
  showingAll?: boolean;
  /** Extra CSS classes on the outer wrapper */
  className?: string;
}

const RecordCount: React.FC<RecordCountProps> = ({
  label,
  showing,
  filtered,
  total,
  hasActiveFilters = false,
  isLoading = false,
  onShowAll,
  onShowPaginated,
  showingAll = false,
  className = '',
}) => {
  const effectiveTotal = total ?? filtered;
  const showShowingCount = showing < filtered;

  const showingText = showShowingCount
    ? ` (showing ${showing.toLocaleString()})`
    : '';

  let countText: string;
  if (hasActiveFilters && effectiveTotal !== filtered) {
    countText = `${filtered.toLocaleString()} ${label} match criteria${showingText} · ${effectiveTotal.toLocaleString()} total ${label}`;
  } else {
    countText = `${filtered.toLocaleString()} ${label} found${showingText}`;
  }

  const canShowAll = !showingAll && onShowAll && filtered > showing;
  const canShowPaginated = showingAll && onShowPaginated;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {isLoading && (
        <svg
          className="w-4 h-4 animate-spin text-text-secondary dark:text-text-dark-secondary"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      )}
      <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
        {countText}
      </p>
      {canShowAll && (
        <button
          onClick={onShowAll}
          className="text-sm text-primary-600 hover:text-primary-700 font-medium hover:underline transition-colors"
        >
          Show All
        </button>
      )}
      {canShowPaginated && (
        <button
          onClick={onShowPaginated}
          className="text-sm text-primary-600 hover:text-primary-700 font-medium hover:underline transition-colors"
        >
          Show Paginated
        </button>
      )}
    </div>
  );
};

export default RecordCount;
